import {
  checkIsApplePayCheckout,
  checkIsApplePayExpressCheckout,
  checkIsCardCheckout,
  checkIsExpressCheckout,
  checkIsGooglePayCheckout,
  paymentMethodTypes,
} from '@JohnLewisPartnership/checkout-psp-ui';

// Design System
import validate from 'jl-design-system/form/validation/validate';

// Types
import type {
  OnButtonClickPaymentHandlerType,
  OnPaymentSuccessPaymentHandlerType,
  OnShippingAddressChangePaymentHandlerType,
  PaymentMethodType,
  StripeBillingDetails,
  StripeShippingDetails,
  ValidateBillingDetailsPaymentHandlerType,
  ValidateShippingAddressPaymentHandlerType,
} from '@JohnLewisPartnership/checkout-psp-ui';
import { AppDispatch, AppGetState, RootState } from 'types/RootState.types';

// Config
import { cancelApplePayPayment, failureApplePayPayment, getApplePayExpressPaymentRequest, triggerOpenApplePayPaymentSheetEvent } from './applePayActions';
import { isFailure, setFullDeliveryAddress, setPartialDeliveryAddress } from './expressPaymentActions';
import { googlePayOnCancel } from './googlePayActions';
import {
  postPayments,
  setCardFormValidationStatus,
  setSelectedPaymentType,
  triggerPlaceOrderAndPayStartAnalytics,
  validator,
} from './paymentActions';
import { putDeliveryDetails } from '../delivery/deliveryActions';
import { saveTimePlaceOrderClicked } from '../timing/timingActions';
import { UPDATE_BILLING_ADDRESS } from '@constants/actionConstants';
import errorCodeConstants from '@constants/errorCodeConstants';
import featureConstants from '@constants/featureConstants';
import { updateBillingAddress } from '@redux/actions/payment/cardPaymentActions';
import { isFeatureActive } from '@redux/reducers/config/configReducer';
import { countryCodeIsInternational, getFormattedAddressFromStripe } from '@utils/address/addressHelpers';
import getBillingDetailsFormConfig from '@utils/form/configs/billingAddress';
import { getDeliveryAddressFormConfig } from '@utils/form/configs/deliveryAddress';
import { isEmptyObject } from '@utils/object';
import handleInconsistentOrderFormState from '@utils/orderform/handleInconsistentOrderFormState';

export const isStripeCheckoutEnabled = (state: RootState) =>
  isFeatureActive(state, featureConstants.STRIPE_CHECKOUT_ENABLED);

export const stripeOnDismissHandler =
  ({ paymentMethodType }: { paymentMethodType: PaymentMethodType }) =>
    async (dispatch: AppDispatch) => {
      const isApplePay = checkIsApplePayCheckout(paymentMethodType);
      const isApplePayExpressCheckout = checkIsApplePayExpressCheckout(paymentMethodType);
      const isGooglePayCheckout = checkIsGooglePayCheckout(paymentMethodType);

      if (isGooglePayCheckout) {
        dispatch(googlePayOnCancel());
      }

      if (isApplePay) {
        dispatch(cancelApplePayPayment(undefined, isApplePayExpressCheckout));
      }
    };

const getValidateBillingDetailsResult = (billingDetails?: StripeBillingDetails) => {
  const formattedAddress = getFormattedAddressFromStripe(billingDetails);

  const validateAddressResult = validate(
    {
      ...formattedAddress?.address,
      ...formattedAddress?.addressee,
      phoneNumber: formattedAddress?.phoneNumber,
    },
    {
      config: getBillingDetailsFormConfig({
        countryCode: formattedAddress?.address?.countryCode,
        actions: {},
        enableGBCounty: !!formattedAddress?.address?.countyStateOrProvince,
      }),
    },
  );

  return {
    formattedAddress,
    validateAddressResult,
  };
};

const getValidateShippingAddressResult = (shippingAddress?: StripeShippingDetails) => {
  const formattedAddress = getFormattedAddressFromStripe(shippingAddress);

  const validateAddressResult = validate(
    {
      ...formattedAddress?.address,
      ...formattedAddress?.addressee,
      phoneNumber: formattedAddress?.phoneNumber,
    },
    {
      config: getDeliveryAddressFormConfig({
        enableGBCounty: !!formattedAddress?.address?.countyStateOrProvince,
      }),
    },
  );

  return {
    formattedAddress,
    validateAddressResult,
  };
};

export const stripeValidateBillingDetailsHandler =
  ({ billingDetails }: ValidateBillingDetailsPaymentHandlerType) =>
    async () => {
      const { validateAddressResult } = getValidateBillingDetailsResult(billingDetails);

      return {
        shouldReject: !isEmptyObject(validateAddressResult),
      };
    };

export const stripeValidateShippingAddressHandler =
  ({ shippingAddress }: ValidateShippingAddressPaymentHandlerType) =>
    async () => {
      const { formattedAddress, validateAddressResult } = getValidateShippingAddressResult(shippingAddress);

      if (countryCodeIsInternational(formattedAddress?.address?.countryCode)) {
        return {
          message: 'We only ship to the UK',
          shouldReject: true,
        };
      }

      return {
        shouldReject: !isEmptyObject(validateAddressResult),
      };
    };

export const stripeOnPaymentSuccessHandler =
  ({
    billingDetails,
    paymentMethodType,
  }: OnPaymentSuccessPaymentHandlerType & {
    paymentMethodType: PaymentMethodType;
  }) =>
    async (dispatch: AppDispatch) => {
      const isApplePayExpressCheckout = checkIsApplePayExpressCheckout(paymentMethodType);
      const normalisedMethod = isApplePayExpressCheckout ? paymentMethodTypes.APPLE_PAY : paymentMethodType;

      const isApplePay = checkIsApplePayCheckout(normalisedMethod);
      const isGooglePay = checkIsGooglePayCheckout(normalisedMethod);

      const { formattedAddress, validateAddressResult } = getValidateBillingDetailsResult(billingDetails);

      if (isEmptyObject(validateAddressResult)) {
        const updateBillingAddressResponse = await dispatch(
          updateBillingAddress({
            body: formattedAddress,
            ...(isApplePay && { isApplePaySelected: true }),
            ...(isGooglePay && { isGooglePay: true }),
          }),
        );

        if (updateBillingAddressResponse.type === `${UPDATE_BILLING_ADDRESS}.SUCCESS`) {
          return billingDetails;
        }
      }

      if (isGooglePay) {
        return new Promise((resolve) => {
          dispatch({
            type: `${UPDATE_BILLING_ADDRESS}.FAILED`,
            error: {
              code: errorCodeConstants.INCOMPLETE_ADDRESS_DETAILS,
            },
            googlePayBillingAddress: formattedAddress,
            googlePayBillingAddressResolve: resolve,
          });
        });
      }

      dispatch(failureApplePayPayment(undefined, isApplePayExpressCheckout));
      return {};
    };

// Card Checkout
export const stripeCardCheckoutOnButtonClickHandler = () => async (dispatch: AppDispatch) => {
  const billingDetailsValid = await dispatch(validator.validateBillingAddress());

  if (!billingDetailsValid) {
    dispatch(setCardFormValidationStatus(false));
    return false;
  }
  return true;
};

// Express Checkout
export const stripeOnExpressCheckoutClickHandler =
  ({ paymentMethodType }: { paymentMethodType: PaymentMethodType }) =>
    (dispatch: AppDispatch) => {
      const isApplePay = checkIsApplePayCheckout(paymentMethodType);
      const isApplePayExpressCheckout = checkIsApplePayExpressCheckout(paymentMethodType);

      if (isApplePay) {
        dispatch(triggerOpenApplePayPaymentSheetEvent({ express: isApplePayExpressCheckout }));
      }
    };

// Apple Pay Checkout
export const stripeApplePayExpressCheckoutOnButtonClickHandler =
  ({ selectedShippingOptionIdentifier, shippingAddress }: OnButtonClickPaymentHandlerType) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
      if (selectedShippingOptionIdentifier && shippingAddress) {
        const { formattedAddress, validateAddressResult } = getValidateShippingAddressResult(shippingAddress);
        if (!isEmptyObject(validateAddressResult)) {
          // eslint-disable-next-line no-console
          console.warn(
            'debug: stripeApplePayExpressCheckoutOnButtonClickHandler() error; validateAddressResult',
            validateAddressResult,
          );
          dispatch(failureApplePayPayment(undefined, true));
          return false;
        }

        const body = {
          addressDetails: {
            ...formattedAddress,
          },
        };

        const fulfilmentOfferId = selectedShippingOptionIdentifier;
        const setFullDeliveryAddressResponse = await dispatch(setFullDeliveryAddress({ body }));

        if (isFailure(setFullDeliveryAddressResponse)) {
          // eslint-disable-next-line no-console
          console.warn(
            'debug: stripeApplePayExpressCheckoutOnButtonClickHandler() error; isFailure(setFullDeliveryAddressResponse)',
            isFailure(setFullDeliveryAddressResponse),
          );
          dispatch(failureApplePayPayment(undefined, true));
          return false;
        }

        const { delivery: { deliveries = [] } = {} } = getState() ?? {};

        const deliveryDetailsPayload = [
          {
            deliveryId: deliveries[0]?.id,
            fulfilment: {
              fulfilmentOfferId,
            },
          },
        ];

        const putDeliveryDetailsResponse =
          await dispatch(putDeliveryDetails({ deliveryDetailsPayload, express: true }));
        if (isFailure(putDeliveryDetailsResponse)) {
          if (putDeliveryDetailsResponse?.error?.code === errorCodeConstants.ORDER_FORM_INCONSISTENT_STATE) {
            dispatch(handleInconsistentOrderFormState());
          }

          // eslint-disable-next-line no-console
          console.warn(
            'debug: stripeApplePayExpressCheckoutOnButtonClickHandler() error; isFailure(putDeliveryDetailsResponse)',
            isFailure(putDeliveryDetailsResponse),
          );
          dispatch(failureApplePayPayment(undefined, true));
          return false;
        }

        const postPaymentsResponse = await dispatch(postPayments());
        if (isFailure(postPaymentsResponse)) {
          // eslint-disable-next-line no-console
          console.warn(
            'debug: stripeApplePayExpressCheckoutOnButtonClickHandler() error; isFailure(postPaymentsResponse)',
            isFailure(postPaymentsResponse),
          );
          dispatch(failureApplePayPayment(undefined, true));
          return false;
        }

        return true;
      }
      // eslint-disable-next-line no-console
      console.warn('debug: stripeApplePayExpressCheckoutOnButtonClickHandler() error; default');
      dispatch(failureApplePayPayment(undefined, true));
      return false;
    };

export const stripeOnShippingAddressChangeHandler =
  ({ shippingAddress }: OnShippingAddressChangePaymentHandlerType) =>
    async (dispatch: AppDispatch) => {
      const { city, country, postal_code } = shippingAddress || {};

      const partialAddress = {
        townOrCity: city,
        countryCode: country,
        ...(postal_code && { postcode: `${postal_code}1AA` }),
      };

      const setPartialDeliveryAddressResponse = await dispatch(setPartialDeliveryAddress(partialAddress));

      if (setPartialDeliveryAddressResponse?.type?.includes('FAILED')) {
        return {
          shouldReject: true,
        };
      }

      const applePayExpressPaymentResponse = await dispatch(getApplePayExpressPaymentRequest());
      const shippingRates =
        applePayExpressPaymentResponse?.result?.modifiers?.[0]?.data?.additionalShippingMethods ?? [];

      return {
        shippingRates,
        shouldReject: shippingRates.length === 0,
      };
    };

export const stripeOnButtonClickHandler =
  ({
    paymentMethodType,
    selectedShippingOptionIdentifier,
    shippingAddress,
  }: OnButtonClickPaymentHandlerType & { paymentMethodType?: PaymentMethodType }) =>
    async (dispatch: AppDispatch) => {
      const isApplePayExpressCheckout = checkIsApplePayExpressCheckout(paymentMethodType);
      const isCardCheckout = checkIsCardCheckout(paymentMethodType);
      const isExpressCheckout = checkIsExpressCheckout(paymentMethodType);
      let shouldReturn: boolean = true;

      if (isApplePayExpressCheckout) {
        shouldReturn = await dispatch(
          stripeApplePayExpressCheckoutOnButtonClickHandler({
            selectedShippingOptionIdentifier,
            shippingAddress,
          }),
        );
      }

      if (isCardCheckout) {
        shouldReturn = await dispatch(stripeCardCheckoutOnButtonClickHandler());
      }

      if (shouldReturn && isExpressCheckout) {
        dispatch(saveTimePlaceOrderClicked());
        await dispatch(setSelectedPaymentType({ paymentType: paymentMethodType }));

        const analyticsLabelMap = {
          [paymentMethodTypes.APPLE_PAY_EXPRESS]: 'applepayExpress',
          [paymentMethodTypes.APPLE_PAY]: 'applepay',
          [paymentMethodTypes.GOOGLE_PAY]: 'gpay',
          [paymentMethodTypes.KLARNA]: 'klarna',
        };

        const label = analyticsLabelMap[paymentMethodType];
        if (label) {
          dispatch(triggerPlaceOrderAndPayStartAnalytics(label));
        }
      }

      return shouldReturn;
    };
